/* eslint-disable promise/avoid-new */
/* eslint-disable no-console */
/* eslint-disable no-plusplus */

// AI Generated Note: The following code implements multiple color extraction algorithms generated by V0 AI.

export type ColorOption = {
	color: string;
	hex: string;
	rgba: string;
	gradient: string;
};

// Helper to load image and create canvas
async function loadImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';

		img.onload = () => {
			resolve(img);
		};

		img.onerror = (error) => {
			console.error('Failed to load image for color extraction:', error);
			console.error('Image URL:', url);
			reject(new Error('Failed to load image'));
		};

		img.src = url;
	});
}

// Helper to get image data from a specific region
function getImageData(img: HTMLImageElement): ImageData {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');

	if (!ctx) {
		console.error('Failed to get canvas 2d context');
		throw new Error('Failed to get canvas 2d context');
	}

	let sx = 0;
	let sw = img.width;

	// Use Region 50% right half
	sx = Math.floor(img.width / 2);
	sw = Math.floor(img.width / 2);

	canvas.width = sw;
	canvas.height = img.height;

	try {
		ctx.drawImage(img, sx, 0, sw, img.height, 0, 0, sw, img.height);
		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		return imageData;
	} catch (error) {
		console.error('Error drawing image to canvas or getting image data:', error);
		throw new Error('Failed to extract image data. This may be due to CORS restrictions.');
	}
}

// Simple average color calculation
function calculateAverageColor(imageData: ImageData): string {
	const { data } = imageData;
	let r = 0;
	let g = 0;
	let b = 0;
	let count = 0;

	for (let i = 0; i < data.length; i += 4) {
		r += data[i];
		g += data[i + 1];
		b += data[i + 2];
		count++;
	}

	r = Math.round(r / count);
	g = Math.round(g / count);
	b = Math.round(b / count);

	return `rgb(${r}, ${g}, ${b})`;
}

// Helper function to convert RGB to HEX
function rgbToHex(rgb: string): string {
	const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
	if (!match) return '#000000';

	// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars
	const [_, r, g, b] = match;
	return `#${[r, g, b]
		.map((x) => Number(x).toString(16).padStart(2, '0'))
		.join('')
		.toUpperCase()}`;
}

function rgbToRgba(rgb: string, alpha = 1): string {
	const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
	if (!match) return `rgba(0,0,0,${alpha})`;
	const [, r, g, b] = match;
	return `rgba(${r},${g},${b},${alpha})`;
}

const DEFAULT_COLOR: ColorOption = {
	color: 'rgb(128, 128, 128)',
	hex: '#808080',
	rgba: 'rgba(128,128,128,1)',
	gradient:
		'linear-gradient(to_left,rgba(128,128,128,1)_0%,rgba(128,128,128,1)_79%,rgba(128,128,128,0.3)_95%,rgba(128,128,128,0)_100%)',
};

export async function extractColors(imageUrl: string): Promise<ColorOption> {
	try {
		const img = await loadImage(imageUrl);
		const imageData = getImageData(img);
		const rgbColor = calculateAverageColor(imageData);

		const color: ColorOption = {
			color: rgbColor,
			hex: rgbToHex(rgbColor),
			rgba: rgbToRgba(rgbColor),
			gradient: `linear-gradient(to_left,${rgbToRgba(rgbColor, 1)}_0%,${rgbToRgba(
				rgbColor,
				1,
			)}_79%,${rgbToRgba(rgbColor, 0.3)}_95%,${rgbToRgba(rgbColor, 0)}_100%)`,
		};

		return color;
	} catch (error) {
		console.error('Color extraction failed, using default gradient:', error);
		return DEFAULT_COLOR;
	}
}
